import _ from "lodash";
import React, { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Input, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import { api, dateHelpers, filterHelpers, UserContext } from "../utils";
import { toast } from "react-toastify";
import { FilterSelect } from '.';

export default function ProdRecordEditModal({
   editItem,
   toggleCallback,
   callbackRefresh,
}) {
   const SELECT_OPTION = { value: 0, label: "-- Select --" };
   const ALL_OPTION = { value: 0, label: "N/A" };
   const NA_OPTION = { value: "N/A", label: "N/A" };
   const ALL_MACHINES = {
      value: 0,
      label: "-- Select --",
      activeMachines: "0",
      spindles: 0,
      sides: false,
   };
   const [filters, setFilters] = useState([
      { filterName: "machineId", value: ALL_MACHINES },
      { filterName: "machineNum", value: ALL_OPTION },
      { filterName: "spindle", value: ALL_OPTION },
      { filterName: "side", value: NA_OPTION },
      { filterName: "defectId", value: SELECT_OPTION },
      { filterName: 'isGood', value: false },
      { filterName: 'weight', value: 0 }
   ]);
   const { currentUser } = useContext(UserContext);
   const sleep = ms => new Promise(r => setTimeout(r, ms));
   const isGood = filterHelpers.getValue(filters, "isGood");
   const weight = filterHelpers.getValue(filters, "weight");
   const [defects, setDefects] = useState([]);            // Defect types for dropdown
   const [disabled, setDisabled] = useState(false);       // Disable good checkbox and defect list when appropriate
   const [initialized, setInitialized] = useState(false); // Record initialized
   const [loaded, setLoaded] = useState(false);           // Drop-down lists populated
   const [machines, setMachines] = useState([]);          // Active machine types 
   const [machineNums, setMachineNums] = useState([]);    // Active machines for selected machine type
   const [message, setMessage] = useState();              // errors to display, if any, when saving
   const [model, setModel] = useState(null);              // Production Record to edit
   const [sides, setSides] = useState([]);                // Sides available, if any, for selected machine
   const [spindles, setSpindles] = useState([]);          // Spindles available for selected machine
   const [working, setWorking] = useState(false);         // Show or hide spinner on spinner buttons

   useEffect(() => {
      if (!currentUser) return;
      api
         .fetch(`Production/InitConeLists`)
         .then((response) => {
            if (response.data.success) {
               const machineList = _.concat(
                  [ALL_MACHINES],
                  _.map(response.data.machines, (m) => ({
                     value: m.machineId,
                     label: m.machineName,
                     activeMachines: m.activeMachines,
                     spindles: m.spindles,
                     sides: m.hasBSide,
                  }))
               );
               const defectList = _.concat(
                  [SELECT_OPTION],
                  _.map(response.data.defects, (d) => ({
                     value: d.id,
                     label: d.defect,
                     isDisabled: d.id === 9 // Under MSR Weight is auto-selected, operator cannot select
                  }))
               );
               return {
                  machineList: machineList,
                  defectList: defectList,
               };
            } else {
               throw new Error(response);
            }
         })
         .then(({ machineList, defectList }) => {
            setMachines(machineList);
            setDefects(defectList);
            // These should only fill in after a machine is selected, just put in a default selections
            setMachineNums([ALL_OPTION]);
            setSides([NA_OPTION]);
            setSpindles([ALL_OPTION]);
         })
         .catch((error) => {
            api.catchHandler(error);
            toast.error(error);
         })
         .finally(() => {
            setLoaded(true);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, []);

   useEffect(() => {
      setModel(editItem);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [editItem]);

   useEffect(() => {
      if (!loaded || !initialized)
         return;  // either the lists are not populated or the model is not populated yet

      async function setLists() {
         if (model?.maxWeight < 90) { // Not an exception machine
            // Over MSR Weight defect is auto-selected only, operator cannot select
            setDefects(defects.map(item => item.value === 5 ? { ...item, isDisabled: true } : item))
         }

         // set child drop-down options as appropriate for the machine tied to the record being edited
         const mach = _.find(machines, (x) => x.value === model.machineId);
         let newMachList = await fixMachines(mach.activeMachines);
         let newSpindleList = await fixSpindles(mach.spindles);
         let newSideList = await fixSides(mach.sides);

         // Set ddl selected values based on the record being edited
         const machFilter = model.machineId > 0
            ? _.find(machines, (m) => m.value === model.machineId)
            : ALL_MACHINES;
         let newFilters = filterHelpers.getFilters(filters, {
            filterName: "machineId",
            value: machFilter,
         });

         const numFilter = model.machineNum > 0
            ? _.find(newMachList, (m) => m.value === model.machineNum)
            : ALL_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "machineNum",
            value: numFilter,
         });

         const spindleFilter = model.spindle > 0
            ? _.find(newSpindleList, (s) => s.value === model.spindle)
            : ALL_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "spindle",
            value: spindleFilter,
         });

         const sideFilter = model.side !== ""
            ? _.find(newSideList, (s) => s.value === model.side)
            : NA_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "side",
            value: sideFilter,
         });

         const defectFilter = model.defectId > 0
            ? _.find(defects, (d) => d.value === model.defectId)
            : SELECT_OPTION;
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "defectId",
            value: defectFilter,
         });

         // Set filters not related to drop-down lists
         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "isGood",
            value: model.isGood,
         });

         newFilters = filterHelpers.getFilters(newFilters, {
            filterName: "weight",
            value: model.pkgWeight,
         });

         setFilters(newFilters);
         inputFocus("pkgWgt");
      }

      setLists();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [initialized, loaded]);

   useEffect(() => {
      if (model !== null)
         setInitialized(true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [model]);

   async function fixMachines(activeMachines) {
      // Fill in the drop-down with active machines for the selected machine
      var nums = activeMachines.split(", ");
      let newMachineList = _.concat(
         [ALL_OPTION],
         _.map(nums, (m) => ({ value: parseInt(m, 10), label: m }))
      );
      setMachineNums(newMachineList);
      return newMachineList;
   };

   async function fixSides(hasBSide) {
      // Fill in the drop-down with available sides for the selected machine
      let newSideList = [NA_OPTION];
      if (hasBSide === true) {
         newSideList = _.concat(newSideList, _.map([{ value: "A", label: "A" }]));
         newSideList = _.concat(newSideList, _.map([{ value: "B", label: "B" }]));
      }
      setSides(newSideList);
      return newSideList;
   };

   async function fixSpindles(spindlesPerSide) {
      // Fill in the drop-down with available spindles for the selected machine
      let newSpindleList = [ALL_OPTION];
      for (let i = 1; i <= spindlesPerSide; i++) {
         newSpindleList = _.concat(
            newSpindleList,
            _.map([{ value: i, label: i.toString() }])
         );
      }
      setSpindles(newSpindleList);
      return newSpindleList;
   };

   const handleFocus = (event) => event.target.select();

   async function inputFocus(fieldname) {
      await sleep(50);  // allows time for input to be recognized
      const el = document.querySelector(`input[name=${fieldname}]`);
      if (el !== null) {
         await el.focus();
      }
   }

   function onDefectChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.defectId = newFilterValue.value < 1 ? 1 : newFilterValue.value;
      changes.defect = newFilterValue.value < 1 ? "Good" : newFilterValue.label;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onFilterChange(changedFilter) {
      const filterChanges = filterHelpers.getFilters(filters, changedFilter);
      setFilters(filterChanges);
   }

   function onGoodVsBad(opt) {
      let changes = Object.assign({}, model);
      changes.isGood = opt;
      setModel(changes);
      onFilterChange({ filterName: "isGood", value: opt, filteredDisplay: '' })
      inputFocus("pkgWgt");
   }

   const onMachineChange = async (newFilterValue) => {
      // Fill in the machine related drop-down lists with proper values
      const mach = _.find(machines, (x) => x.value === newFilterValue.value);
      let newMachList = await fixMachines(mach.activeMachines);
      let newSpindleList = await fixSpindles(mach.spindles);
      let newSideList = await fixSides(mach.sides);

      // Set the machine dependent filters to their defaults
      const numFilter = _.find(newMachList, (m) => m.value === 0);
      const spindleFilter = _.find(newSpindleList, (s) => s.value === 0);
      const sideFilter = _.find(newSideList, (s) => s.value === "N/A");

      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "machineId",
         value: mach,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "machineNum",
         value: numFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "spindle",
         value: spindleFilter,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "side",
         value: sideFilter,
      });
      setFilters(newFilters);

      // Set the data values
      let changes = Object.assign({}, model);
      changes.machineId = newFilterValue.value;
      changes.machineName = newFilterValue.label;
      changes.machineNum = ALL_OPTION.value;
      changes.side = NA_OPTION.value;
      changes.spindle = ALL_OPTION.value;
      setModel(changes);
   };

   function onMachineNumChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.machineNum = newFilterValue.value;
      setModel(changes);
   }

   function onSideChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.side = newFilterValue.value;
      setModel(changes);
   }

   function onSpindleChange(newFilterValue) {
      let newFilters = filterHelpers.getFilters(filters, newFilterValue);
      setFilters(newFilters);

      let changes = Object.assign({}, model);
      changes.spindle = newFilterValue.value;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onWeightChange(val) {
      if (val === undefined || val === null || val === "") val = 0;

      let changes = Object.assign({}, model);
      if (val < changes.minWeight) {
         // operator may still be updating, but we need to enforce this until then
         resetWeight(9, false, val, true);
         changes.isGood = false;
         changes.defectId = 9;
         changes.defect = "Under MSR Weight";
      }
      else if (val > changes.maxWeight) {
         // need to enforce the overweight issue
         resetWeight(5, false, val, true);
         changes.isGood = false;
         changes.defectId = 5;
         changes.defect = "Over MSR Weight";
      }
      else {
         // operator may still be updating, but for now it is within spec
         resetWeight(0, true, val, false);
         changes.isGood = true;
         changes.defectId = 1;
         changes.defect = "Good";
      }
      changes.pkgWeight = val;
      setModel(changes);
      inputFocus("pkgWgt");
   }

   function onSave() {
      setMessage();
      let warnings = [];
      if (model?.machineId === 0) {
         warnings.push('Machine is required');
      }
      if (model?.machineNum === 0) {
         warnings.push('Machine # is required');
      }
      if (model?.side === "N/A" && sides.length > 1) {
         warnings.push('Side is required');
      }
      if (model?.spindle === 0 && spindles.length > 1) {
         warnings.push('Spindle is required');
      }
      if (model?.pkgWeight === 0) {
         warnings.push('Package weight is required');
      }
      if (isGood === false && model?.defectId <= 1) {
         warnings.push('Defect is required');
      }
      if (warnings.length) {
         setMessage(warnings);
         return;
      }

      setWorking(true);
      api
         .post("Production/UpdateProdRecord", model)
         .then((r) => {
            if (r.data.success) {
               callbackRefresh();
            } else {
               toggleCallback();
               setMessage(r.data.message);
            }
         })
         .catch(api.catchHandler);
   }

   function resetWeight(dfctId, goodVal, wgtVal, isDisabled) {
      const dfct = _.find(defects, (x) => x.value === dfctId);
      let newFilters = filterHelpers.getFilters(filters, {
         filterName: "defectId",
         value: dfct,
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "isGood",
         value: goodVal,
         filteredDisplay: ''
      });
      newFilters = filterHelpers.getFilters(newFilters, {
         filterName: "weight",
         value: wgtVal,
      });
      setFilters(newFilters);
      setDisabled(isDisabled);
   }

   return (
      <Modal isOpen={true} toggle={toggleCallback}>
         <ModalHeader toggle={toggleCallback}>
            <span>Edit Production Record</span>
         </ModalHeader>
         <ModalBody>
            {initialized && loaded ? (
               <div>
                  <Table striped>
                     <tbody>
                        <tr>
                           <td colSpan="2">Item: {model?.itemWithDesc}</td>
                        </tr>
                        <tr>
                           <td colSpan="2">Cone #: {model?.coneId}</td>
                        </tr>
                        <tr>
                           <td colSpan="2">Completed By: {model?.userName} on {dateHelpers.dateTimeFormat(model?.dateCompleted, "MM-DD-YYYY HH:mm")}</td>
                        </tr>
                        <tr>
                           <td>Machine Type:</td>
                           <td>
                              <FilterSelect
                                 filterName="machineId"
                                 options={machines}
                                 value={filterHelpers.getValue(filters, "machineId")}
                                 onChangeCallback={(e) => onMachineChange(e)}
                              />
                           </td>
                        </tr>
                        <tr>
                           <td>Machine #:</td>
                           <td>
                              <FilterSelect
                                 filterName="machineNum"
                                 options={machineNums}
                                 value={filterHelpers.getValue(filters, "machineNum")}
                                 onChangeCallback={(e) => onMachineNumChange(e)}
                              />
                           </td>
                        </tr>
                        <tr>
                           <td>Side:</td>
                           <td>
                              <FilterSelect
                                 filterName="side"
                                 options={sides}
                                 value={filterHelpers.getValue(filters, "side")}
                                 onChangeCallback={(e) => onSideChange(e)}
                              />
                           </td>
                        </tr>
                        <tr>
                           <td>Spindle #:</td>
                           <td>
                              <FilterSelect
                                 filterName="spindle"
                                 options={spindles}
                                 value={filterHelpers.getValue(filters, "spindle")}
                                 onChangeCallback={(e) => onSpindleChange(e)}
                              />
                           </td>
                        </tr>
                        <tr>
                           <td>Package Weight:</td>
                           <td>
                              <Input className="no-spinner h-100"  // This className hides the up/down arrows at end of entry field
                                 type="number"
                                 name="pkgWgt"
                                 value={weight}
                                 onFocus={handleFocus}
                                 onChange={(e) => {
                                    if (e.target.value.match(/^\d{1,3}(\.\d{0,2})?$/)) // Numeric 1-3 digits and up to 2 decimals allowed
                                       onWeightChange(e.target.value);
                                 }}
                              />
                           </td>
                        </tr>
                        <tr>
                           <td colSpan="2">
                              <Input
                                 type="checkbox"
                                 value={isGood}
                                 className="h6 fw-bold my-0 ms-1 mx-2"
                                 onChange={() => onGoodVsBad(!isGood)}
                                 checked={isGood}
                                 disabled={disabled}
                              />
                              <span className="h6 fw-bold ps-2">Item produced is good</span>
                           </td>
                        </tr>
                        {isGood === false ? (
                           <tr>
                              <td>Defect:</td>
                              <td>
                                 <FilterSelect
                                    filterName="defectId"
                                    options={defects}
                                    value={filterHelpers.getValue(filters, "defectId")}
                                    onChangeCallback={(e) => onDefectChange(e)}
                                    disabled={disabled}
                                 />
                              </td>
                           </tr>
                        ) : null}
                     </tbody>
                  </Table>

                  <Row>
                     <Col xs={6} className="text-center">
                        <Button
                           className="shredButton w-100"
                           onClick={onSave}
                           disabled={working}
                        >
                           Update
                        </Button>
                     </Col>
                     <Col xs={6} className="text-center">
                        <Button
                           className="shredButton w-100"
                           onClick={toggleCallback}
                           disabled={working}
                        >
                           Cancel
                        </Button>
                     </Col>
                  </Row>
                  {!_.isNil(message) && message.length > 0 && (
                     <Row className="mt-3 mb-0 pb-0">
                        <Col>
                           <Alert color="danger">
                              {message.map(line => (
                                 <p className="mb-0">{line}</p>
                              ))}
                           </Alert>
                        </Col>
                     </Row>
                  )}
               </div>
            ) : <div className="h5 text-center mt-3">Initializing data ...</div>}
         </ModalBody>
      </Modal>
   );
}
